import type {
  MeasureTextDriftPreview,
  PositionOrphanPreview,
} from '../models/position-orphan'

import type { ValidatedField } from './validated-field'

/** A code that carries nothing beyond its name. */
type NoParams = Record<string, never>

/**
 * The refusals that are a question rather than a failure.
 *
 * Each one carries what the write would do and goes through once the caller
 * calls again with an answer, so the screen that issued it renders it as a
 * dialog. A snackbar beside that dialog would put the same question twice,
 * once as something that went wrong — which is what the shared mutation
 * handler reads this to avoid.
 */
export const ASKED_RATHER_THAN_FAILED = [
  'POSITIONS_WOULD_BE_ORPHANED',
  'MEASURE_TEXT_WOULD_DRIFT',
] as const satisfies readonly (keyof ServerErrorParams)[]

/**
 * The failures a request can report back to the client by name.
 *
 * A code names the condition the backend hit, never the sentence shown for it:
 * the backend throws it through `codedBadRequest` and friends, the frontend
 * maps it to German in `shared/locales/de/server-errors.ts`. That table is
 * exhaustive over this union, so a code cannot be thrown without a sentence.
 */
export type ServerErrorParams = {
  WRONG_PASSWORD: NoParams
  PASSWORD_TOO_SHORT: { min: number }
  QUANTITY_BELOW_MIN: { min: number }
  QUANTITY_ABOVE_MAX: { max: number }
  QUANTITY_DECIMAL_PLACES_EXCEEDED: { count: number }
  FORMULA_STILL_REFERENCED: { count: number }
  MEDIA_ASSET_STILL_REFERENCED: { count: number }
  FORMULA_SYNTAX_ERROR: { detail: string }
  FORMULA_CIRCULAR_REFERENCE: { cycle: string }
  FORMULA_PROTECTED: { name: string }
  CLASSIFICATION_STILL_USED: { count: number }
  CLASSIFICATION_NAME_TAKEN: { name: string }

  /** Product properties: the axis itself, and a value written against one. */
  PRODUCT_PROPERTY_KEY_TAKEN: { key: string }
  PRODUCT_PROPERTY_STILL_USED: { count: number }
  PRODUCT_PROPERTY_OPTION_STILL_USED: { count: number }
  PRODUCT_PROPERTY_ALREADY_ATTACHED: NoParams
  /** A group narrowed a SELECT axis down to no choice at all, which the empty
   *  set cannot mean: stored, it reads as "offers all of them". */
  PRODUCT_GROUP_OFFERS_NOTHING: { label: string }
  /** The axis belongs to no group the entry is filed under. */
  PRODUCT_PROPERTY_NOT_IN_GROUPS: NoParams
  /** A row of a measure's requirement matrix addressed through another
   *  measure's path — a screen that has not been reloaded. */
  REQUIREMENT_VARIANT_NOT_FOUND: NoParams
  /** A price span on a measure that is quoted in no unit, so no product's
   *  price converts to it and the row would reach nothing. */
  VARIANT_PRICE_NEEDS_UNIT: NoParams
  /** One per `PropertyValueError`, so the sentence can name the axis. */
  PRODUCT_PROPERTY_VALUE_WRONG_TYPE: { label: string }
  PRODUCT_PROPERTY_VALUE_BELOW_MINIMUM: { label: string; min: number }
  PRODUCT_PROPERTY_VALUE_ABOVE_MAXIMUM: { label: string; max: number }
  PRODUCT_PROPERTY_OPTION_NOT_OFFERED: { label: string }
  /** An id that is not a choice of the axis at all — a caller working from a
   *  list that has moved on. */
  PRODUCT_PROPERTY_OPTION_UNKNOWN: { label: string }
  /** A migration whose payload stores nothing, or whose axis is taken. */
  MIGRATION_NEEDS_VALUE: NoParams
  MIGRATION_AXIS_TAKEN: NoParams
  SUGGESTION_ALREADY_DECIDED: NoParams
  /** A decision that is neither CONFIRM nor REJECT — no request body is
   *  validated before the service reads it. */
  SUGGESTION_DECISION_UNKNOWN: NoParams
  PRODUCT_NAME_REQUIRED: NoParams
  PRODUCT_IMPORT_DRAFT_MALFORMED: NoParams
  FORMULA_NAME_COLLIDES_WITH_MEASURE: { name: string }
  MEASURE_CODE_COLLIDES_WITH_FORMULA: { code: string }
  MEASURE_CODE_TAKEN: { code: string }
  FORMULA_NAME_TAKEN: { name: string }
  MEASURE_CODE_STILL_REFERENCED: {
    code: string
    measureIds: number[]
    formulaNames: string[]
    blockIds: number[]
  }
  MEASURE_STILL_PLACED: { count: number }
  /** A measure may not rule itself out, and may only rule out one that exists. */
  MEASURE_EXCLUDES_ITSELF: NoParams
  MEASURE_EXCLUSION_NOT_FOUND: NoParams
  CALCULATION_ADMIN_ONLY: NoParams
  /** A row of a stage states one of the three things it can hold. */
  SLOT_NEEDS_ONE_SOURCE: NoParams
  SLOT_ALREADY_DETACHED: NoParams
  SLOT_REFERENCES_NOTHING: NoParams
  SLOT_MOVE_ACROSS_OWNERS: NoParams
  /** The terms of a row that follows a library entry are the library's. */
  SLOT_FOLLOWS_THE_LIBRARY: NoParams
  /** A budget is the measure's own requirement rows, and a template has none. */
  SLOT_BUDGET_NEEDS_A_MEASURE: NoParams
  SLOT_BUDGET_FOREIGN_VARIANT: NoParams
  /** A member of a Preisgruppe charges one of the three, not two. */
  GROUP_MEMBER_NEEDS_ONE_TARGET: NoParams
  /** The protection a library entry has: an edit reaches every measure
   *  referencing it, so a delete may not simply take a term off their prices. */
  BLOCK_STILL_REFERENCED: { count: number }
  BLOCK_GROUP_STILL_REFERENCED: { count: number }
  /** A template would replace stages somebody wrote. */
  MEASURE_HOLDS_OWN_CALCULATION: { count: number }
  /** The order of the stages decides the price, so a reorder states them all. */
  SORT_NEEDS_EVERY_ROW: NoParams
  IMAGE_USAGE_RENDERED_BY_CODE: { key: string }
  PRODUCT_LINK_UNREADABLE: { reason: string }
  PRODUCT_LINK_EXTRACTION_FAILED: NoParams
  PRODUCT_NO_SOURCE_URL: NoParams
  MEDIA_URL_NOT_AN_IMAGE: { url: string }
  MEDIA_UPLOAD_NOT_STAGED: { maxMb: number }
  MEASURE_IN_CONFIGURATOR: NoParams
  MEASURE_NOT_AVAILABLE_AS_POSITION: NoParams
  MEASURE_RULE_BLOCKS_POSITION: { title: string }
  /** `excludedBy` names the measures already at that place. */
  MEASURE_EXCLUDED_BY_POSITION: { title: string; excludedBy: string[] }
  /** Only an administrator adds a measure past what blocks it. */
  POSITION_OVERRIDE_ADMIN_ONLY: NoParams
  /** A price adjustment states a kind and a figure together, or neither. */
  POSITION_ADJUSTMENT_INCOMPLETE: NoParams
  /** A percentage off is a percentage: outside 0–100 it is not a discount. */
  POSITION_DISCOUNT_OUT_OF_RANGE: NoParams
  /** Nothing to reprice against: the position stands behind no configurator
   *  pick, or the project can no longer price the one it does. */
  POSITION_HAS_NO_LIVE_PRICE: NoParams
  /** Nothing to take over: the measure states the position as it already does. */
  POSITION_TEXT_UNCHANGED: NoParams
  /** The wording is the catalogue's, frozen when the position was quoted: it
   *  is taken over from the measure, not typed over. */
  POSITION_TEXT_IS_THE_CATALOGUE_S: NoParams
  /** What a configurator pick costs, and whether the customer sees it, are not
   *  an operator's to type on the position. */
  POSITION_IS_A_PICK: NoParams
  /** The configurator owns the row: the customer takes it back by unticking
   *  the measure, and a delete would be undone by the next save. */
  POSITION_PICK_NOT_DELETABLE: NoParams
  /** Only a pick has a quoted price an adjustment could move it off. */
  POSITION_IS_OWN_LINE: NoParams
  /** The position charges something other than what the catalogue would charge
   *  for the same pick, and no administrator has released it. */
  POSITION_LOCKED: NoParams
  /** A reduction is a discount, not a negative price. */
  POSITION_PRICE_NEGATIVE: NoParams
  /**
   * A change to the catalogue or to the configurator's structure would take
   * positions already quoted out of the configurator.
   *
   * Not a preview route: the write is what knows, so it refuses and hands back
   * what would happen, grouped by project. The screen renders it as a
   * confirmation; an agent puts the same thing to its user. Calling again with
   * `confirmedPositions` equal to `count` goes ahead.
   */
  POSITIONS_WOULD_BE_ORPHANED: PositionOrphanPreview
  /**
   * A measure's wording is about to change while positions hold the old one.
   *
   * Refused rather than decided for the caller, the same way as above: the
   * offers already given keep their wording unless somebody says otherwise.
   * Calling again with `propagateText=none` changes the measure alone;
   * `propagateText=all` also rewrites those positions, which only an
   * administrator may.
   */
  MEASURE_TEXT_WOULD_DRIFT: MeasureTextDriftPreview
  /** A system comment records what happened; one that can be rewritten stops
   *  being a record. */
  COMMENT_IS_A_RECORD: NoParams
  MCP_CONSENT_REFUSED: NoParams
  MCP_UPLOAD_LINK_INVALID: NoParams
  CHANGE_REVERT_BLOCKED: { reason: string }
  CHANGE_REVERT_CONFLICT: { count: number }
  CHANGE_BUNDLE_NOT_FOUND: NoParams
  CATALOGUE_KIND_UNKNOWN: NoParams
  MEASURE_ALREADY_PLACED: NoParams
  PRODUCT_STILL_LINKED: { count: number }
  PRODUCT_STILL_CHARGED: { count: number }
  /** An Arbeitsleistung whose rate a price still charges. */
  LABOUR_ITEM_STILL_CHARGED: { count: number }
  CATALOGUE_FOLDER_NOT_EMPTY: { count: number }
  CATALOGUE_FOLDER_KIND_MISMATCH: NoParams
  CATALOGUE_KIND_HAS_NO_FOLDERS: NoParams
  CLASSIFICATION_KIND_MISMATCH: NoParams
  CATALOGUE_FOLDER_NAME_TAKEN: { name: string }
  CHANGE_BUNDLE_NOT_REVERTED: { count: number }
  QUESTION_STILL_PLACED: { count: number }
  QUESTION_SEVERAL_DEFAULTS: { count: number }
  MANDATE_CLOSED_IN_HUBSPOT: NoParams
  MANDATE_STAGE_UNKNOWN: NoParams
  MANDATE_STAGE_WRITE_FAILED: NoParams
  PROJECT_HAS_NO_CUSTOMER: NoParams
  /** The advisor asking is not the one the project names as responsible. */
  PROJECT_NOT_ASSIGNED: NoParams
  /** The project names nobody, so only the administration changes it. */
  PROJECT_UNASSIGNED: NoParams
  /** Who is responsible for a project is the administration's answer. */
  PROJECT_CONTACT_PERSON_LOCKED: NoParams
  /** Only an advisor is given a project to work on. */
  PROJECT_COLLABORATOR_NOT_SALES: NoParams
  /** The responsible advisor has the access already. */
  PROJECT_COLLABORATOR_IS_RESPONSIBLE: NoParams
  /** Removing a project, or choosing who else works on it, is its
   *  responsible advisor's and the administration's. */
  PROJECT_RESPONSIBLE_ONLY: NoParams
  /** A set of roles an account may not hold: none, or an exclusive one
   *  beside another. */
  ROLES_EMPTY: NoParams
  ROLE_NOT_COMBINABLE: NoParams
  /** A project is listed publicly or belongs to a customer, never both. */
  PROJECT_PUBLIC_HAS_CUSTOMER: NoParams
  PROJECT_CUSTOMER_CHANGED: NoParams
  PROJECT_STATE_CHANGED: NoParams
  CUSTOMER_UNSUBSCRIBED: NoParams
  RESET_TOKEN_INVALID: NoParams
  LISTING_URL_NOT_HTTPS: NoParams
  /** A move between house and apartment would strand configurator answers
   *  the other tree does not ask. */
  PROPERTY_TYPE_STRANDS_SELECTIONS: { count: number }
  EMAIL_SENDING_DISABLED: NoParams
  UNSUBSCRIBE_TOKEN_INVALID: NoParams

  /** Field-level validation: one shape per kind of failure rather than one
   *  code per field. `ValidatedField` is what makes the German exhaustive —
   *  a name with no word for it fails the build. */
  FIELD_REQUIRED: { field: ValidatedField }
  FIELD_EMPTY: { field: ValidatedField }
  FIELD_TOO_LONG: { field: ValidatedField; max: number }
  FIELD_NOT_FINITE: { field: ValidatedField }
  FIELD_OUT_OF_RANGE: { field: ValidatedField; min: number; max: number }
  /** The lower bound is exclusive: 0 itself is rejected. */
  FIELD_OUT_OF_OPEN_RANGE: { field: ValidatedField; min: number; max: number }
  FIELD_NEGATIVE: { field: ValidatedField }
  FIELD_NOT_NON_NEGATIVE_INTEGER: { field: ValidatedField }
  FIELD_NOT_INTEGER_AT_LEAST: { field: ValidatedField; min: number }
  FIELD_VALUE_INVALID: { field: ValidatedField; value: string }
  FIELD_DUPLICATE: { field: ValidatedField; value: string }
  FIELD_NOT_CAMEL_CASE: { field: ValidatedField }
  FIELD_NOT_SCREAMING_SNAKE_CASE: { field: ValidatedField }
  RANGE_MIN_ABOVE_MAX: NoParams
  DECIMAL_PLACES_NOT_ALLOWED: { allowed: string }

  /** House-element types: what the mode allows, and what still points at one. */
  ELEMENT_TYPE_KEY_COLLIDES_WITH_BUILTIN: { key: string }
  ELEMENT_TYPE_OPTION_MODE_NEEDS_OPTION: NoParams
  ELEMENT_TYPE_OPTIONS_ONLY_ON_OPTION_MODE: NoParams
  ELEMENT_TYPE_FIELD_ONLY_ON_QUANTITY_MODE: { field: ValidatedField }
  SUMMARY_VALUE_MAP_INVALID: { max: number; labelMax: number }
  /** The tree's own rules: what a node may be, and where it may hang. */
  GROUP_KIND_NEEDS_CUSTOMER_SCOPE: NoParams
  GROUP_KIND_PLACEMENT: NoParams
  GROUP_CYCLE: NoParams
  QUESTION_SCOPE_MISMATCH: NoParams

  /** Merging and splitting filed documents. */
  MERGE_NEEDS_TWO_DOCUMENTS: NoParams
  MERGE_TOO_MANY_DOCUMENTS: { max: number }
  MERGE_TOTAL_SIZE_EXCEEDED: { maxMb: number }
  MERGE_MIME_TYPE_UNSUPPORTED: { mimeType: string }
  SPLIT_NEEDS_A_BUCKET: NoParams
  SPLIT_ONLY_PDF: NoParams
  SPLIT_PAGE_OUT_OF_RANGE: { page: number; total: number }
  SPLIT_SOURCE_TOO_LARGE: { maxMb: number }

  DOCUMENT_MIME_TYPE_UNSUPPORTED: NoParams
  DOCUMENT_UPLOAD_TOO_LARGE: { maxMb: number }
  DOCUMENT_UPLOAD_NOT_FOUND: NoParams
  DOCUMENT_NOT_PDF: NoParams
  DOCUMENT_TOO_LARGE: { maxMb: number }
  DOCUMENT_READ_FAILED: { reason: string }
  DOCUMENT_UPLOAD_SIZE_UNKNOWN: NoParams
  DOCUMENT_UPLOAD_KEY_INVALID: NoParams
  DOCUMENT_UPLOAD_CHANGED: NoParams
  /** The document is an invoice's file in the Finanzen tab. */
  DOCUMENT_IS_INVOICE: { number: string }
  MEDIA_UPLOAD_CHANGED: NoParams

  /**
   * Pulling the configurator out of another environment.
   *
   * The last three are met inside the worker, where a failure has one string
   * to say why. `failJob` resolves a coded one against the same table a
   * request resolves against, so these read the same on either path.
   */
  /** The environment asked is the source itself. */
  SYNC_NOT_A_TARGET: NoParams
  SYNC_ALREADY_RUNNING: { live: number }
  /** The source turned the sign-in down, or the account is not ROOT there. */
  SYNC_ORIGIN_REFUSED: NoParams
  SYNC_ORIGIN_UNREACHABLE: { origin: string }
  SYNC_SNAPSHOT_REFUSED: NoParams
  /** The source does not carry a column this schema requires: it is behind,
   *  and a sync cannot invent the value. */
  SYNC_SOURCE_BEHIND_SCHEMA: { detail: string }
  /** The source's rows name pictures but it named no bucket to copy them from:
   *  its own `UPLOADS_BUCKET` is unset. */
  SYNC_SOURCE_HAS_NO_MEDIA_BUCKET: NoParams

  /** A staff-triggered background run that is already in progress. */
  JOB_ALREADY_RUNNING: { triggeredBy: string }
  JOB_SWITCHED_OFF: NoParams
  /** Only sales and admin accounts carry an advisor profile. */
  USER_ROLE_HAS_NO_ADVISOR_PROFILE: NoParams
  /** Who an account is: the rules `userIdentityError` states, refused here in
   *  the same order a form would refuse them. */
  USER_LAST_NAME_REQUIRED: NoParams
  USER_ORGANISATION_NOT_ALLOWED: NoParams
  USER_ORGANISATION_HAS_NO_PERSON_FIELDS: NoParams
  USER_PLURAL_SALUTATION_NOT_ALLOWED: NoParams
  USER_PLURAL_SALUTATION_HAS_NO_FIRST_NAME: NoParams
  USER_SALUTATION_REQUIRED_WITHOUT_FIRST_NAME: NoParams
  /** An advisor HubSpot knows is edited in HubSpot. */
  USER_NAME_OWNED_BY_HUBSPOT: NoParams
  /** A sync asked for an advisor HubSpot has no contact for. */
  USER_NOT_KNOWN_TO_HUBSPOT: NoParams
  /** The account already records that address beside its sign-in one. */
  USER_EMAIL_ALREADY_RECORDED: NoParams
  /** Staff are synced through the advisor route, where HubSpot owns the
   *  fields outright. */
  USER_HUBSPOT_SYNC_CUSTOMERS_ONLY: NoParams
  USER_CUSTOMER_NUMBER_TAKEN: { number: string }
  USER_CUSTOMER_NUMBER_NOT_ALLOWED: NoParams
  ELEMENT_TYPE_KEY_TAKEN: NoParams
  ELEMENT_TYPE_STILL_USED: { key: string; count: number }
  ELEMENT_TYPE_OPTIONS_STILL_USED: { keys: string }

  /** Project finances: contracts, installments, invoices, approvals. */
  CONTRACT_PARTY_REQUIRED: NoParams
  CONTRACT_PARTY_NOT_ALLOWED: NoParams
  CONTRACT_PARTY_STILL_USED: { count: number }
  CONTRACT_PARENT_MISMATCH: NoParams
  CONTRACT_HAS_INVOICES: { count: number }
  CONTRACT_HAS_SUPPLEMENTS: { count: number }
  CONTRACT_TEMPLATE_MISMATCH: NoParams
  MILESTONE_NOT_IN_PROJECT: NoParams
  DEDUCTION_PARTY_PER_INVOICE_ONLY: NoParams
  DEDUCTION_NEEDS_ONE_FIGURE: NoParams
  DEDUCTION_AMOUNT_OF_CONTRACT_ONLY: NoParams
  MILESTONE_HAS_INVOICED_INSTALLMENTS: { count: number }
  MILESTONE_RELEASES_RETENTION: { count: number }
  MILESTONE_CODE_TAKEN: { code: string }
  INSTALLMENT_INVOICED: NoParams
  INSTALLMENT_NEEDS_ONE_FIGURE: NoParams
  INSTALLMENT_NOT_IN_CONTRACT: NoParams
  INVOICE_RETENTION_WITHOUT_CONTRACT: NoParams
  INSTALLMENT_CREDIT_UNKNOWN: NoParams
  PLANNING_FEE_NOT_ALLOWED: NoParams
  PLANNING_FEE_EXCEEDS_INSTALLMENT: NoParams
  INSTALLMENT_CREDITED: NoParams
  INVOICE_CANCELLED: NoParams
  INVOICE_CORRECTION_TARGET: NoParams
  INVOICE_HAS_CORRECTIONS: { action: 'cancel' | 'delete' }
  INSTALLMENT_HAS_DEDUCTION: { title: string }
  INVOICE_NOT_DELETABLE: NoParams
  INVOICE_HAS_PAYMENTS: { count: number }
  INVOICE_NOT_STAMPED: NoParams
  ALLOCATION_SIGN_MISMATCH: NoParams
  INVOICE_HAS_NO_DOCUMENT: NoParams
  ALLOCATION_EXCEEDS_INVOICE: { allocated: number; issued: number }
  ALLOCATION_HAS_APPROVALS: NoParams
  APPROVAL_EXCEEDS_LINE: { left: number }
  APPROVAL_DEDUCTS_MORE_THAN_APPROVED: NoParams
  PAYMENT_EXCEEDS_OPEN: { open: number }
  /** A DIRECT contract takes no sum, installment or deduction, and is never
   *  created by hand. */
  CONTRACT_DIRECT_FIXED: NoParams
  /** Approve on create, refused where a line bills more than its
   *  installment has left. */
  INVOICE_APPROVE_OVER_INSTALLMENT: NoParams
  /** Approve on create, refused where a line on an installment bills less
   *  than the installment has left. */
  INVOICE_APPROVE_LINE_CUT: NoParams
  INVOICE_MOVE_CORRECTION: NoParams
  INVOICE_MOVE_SAME_CONTRACT: NoParams
  INVOICE_MOVE_DIRECTION: NoParams
  INVOICE_MOVE_RETENTION_RELEASE: NoParams
  INVOICE_MOVE_RETENTION_HELD: NoParams
  INVOICE_MOVE_CANCELLED_CONTRACT: NoParams
  INVOICE_MOVE_OVER_INSTALLMENT: NoParams
  INVOICE_RETENTION_EXCEEDED: { invoicedBack: number; held: number }
  INVOICE_MOVE_BANK_PAYMENT: NoParams
  INVOICE_MOVE_SHARED_PAYMENT: NoParams
  INVOICE_MOVE_LINE_UNKNOWN: NoParams
  BANK_NOT_CONFIGURED: NoParams
  BANK_UNAVAILABLE: { status: number | null }
  BANK_TRANSACTION_NOT_BOOKABLE: NoParams
  BANK_TRANSACTION_BOOKED: NoParams
  BANK_TRANSACTION_WRONG_WAY: NoParams
  BANK_TRANSACTION_EXCEEDED: { left: number }
  BANK_AMOUNT_REQUIRED: NoParams

  /** Rooms and storeys. */
  ROOM_CODE_TAKEN: { code: string }
  ROOM_CODE_ALLOCATION_FAILED: NoParams
  FLOOR_NOT_IN_PROPERTY: NoParams
  BACKUP_ALREADY_RUNNING: { live: number }
  EMAIL_SEND_FAILED: NoParams
  /** Stamped by the exception filter rather than thrown by hand: every
   *  uncoded 404 and 403 carries one, so the client shows German for a missing
   *  record or a denied permission instead of a fallback. */
  NOT_FOUND: NoParams
  FORBIDDEN: NoParams
  /** Wrong email or password at login. Not filter-stamped: an expired session
   *  is a 401 too and must not tell the user their password is wrong. */
  INVALID_CREDENTIALS: NoParams
}

export type ServerErrorCode = keyof ServerErrorParams

/** The subset whose sentence is assembled from the field name alone. */
export type FieldErrorCode = Extract<ServerErrorCode, `FIELD_${string}`>
